import { z } from 'zod'

/**
 * Avenant à un Contrat signé.
 *
 * Un Contrat signé est opposable : son document a été accepté tel quel et ne se
 * réécrit jamais. Un avenant est un document **séparé** qui le référence,
 * numéroté par Contrat, et dont l'application ne modifie aucun champ gelé du
 * Contrat original — ni sa version de paramètres, ni son prix, ni ses dates.
 */

export const contractAmendmentKinds = ['AVENANT', 'NOUVELLE_VERSION_CONTRACTUELLE'] as const
export type ContractAmendmentKind = (typeof contractAmendmentKinds)[number]

export const contractAmendmentKindLabels: Record<ContractAmendmentKind, string> = {
  AVENANT: 'Avenant',
  NOUVELLE_VERSION_CONTRACTUELLE: 'Nouvelle version contractuelle',
}

export const contractAmendmentStatuses = ['DRAFT', 'APPLIED'] as const
export type ContractAmendmentStatus = (typeof contractAmendmentStatuses)[number]

export const contractAmendmentStatusLabels: Record<ContractAmendmentStatus, string> = {
  DRAFT: 'Brouillon',
  APPLIED: 'Appliqué',
}

export interface ContractAmendment {
  id: string
  contractId: string
  amendmentNumber: number
  kind: ContractAmendmentKind
  reason: string
  /** Description des changements. Libre dans cette tranche, toujours un objet. */
  changes: { description: string }
  status: ContractAmendmentStatus
  documentStoragePath?: string
  appliedAt?: string
  createdAt: string
}

export interface CreateContractAmendmentCommand {
  contractId: string
  kind: ContractAmendmentKind
  reason: string
  description: string
}

export interface CreateContractAmendmentResult {
  amendmentId: string
  contractId: string
  amendmentNumber: number
  kind: ContractAmendmentKind
  status: ContractAmendmentStatus
}

export interface ApplyContractAmendmentResult {
  amendmentId: string
  contractId: string
  amendmentNumber: number
  status: ContractAmendmentStatus
  appliedAt: string
  replayed: boolean
}

export const contractAmendmentRowSchema = z.object({
  id: z.uuid(),
  contract_id: z.uuid(),
  amendment_number: z.number().int().positive(),
  kind: z.enum(contractAmendmentKinds),
  reason: z.string().min(1),
  changes: z.object({ description: z.string().default('') }).catch({ description: '' }),
  status: z.enum(contractAmendmentStatuses),
  document_storage_path: z.string().nullable(),
  applied_at: z.string().nullable(),
  created_at: z.string(),
})

export const createContractAmendmentResultSchema = z.object({
  amendmentId: z.uuid(),
  contractId: z.uuid(),
  amendmentNumber: z.number().int().positive(),
  kind: z.enum(contractAmendmentKinds),
  status: z.enum(contractAmendmentStatuses),
})

export const applyContractAmendmentResultSchema = z.object({
  amendmentId: z.uuid(),
  contractId: z.uuid(),
  amendmentNumber: z.number().int().positive(),
  status: z.enum(contractAmendmentStatuses),
  appliedAt: z.string(),
  replayed: z.boolean(),
})

/**
 * Un Contrat signé, au sens d'ADR-015 : entré en vigueur et non annulé.
 *
 * Le serveur refuse un avenant sur tout autre statut, parce qu'un Contrat qui
 * n'est pas encore opposable se modifie directement. L'écran ne propose donc le
 * panneau que dans les mêmes cas, plutôt que d'offrir une action qui échouerait.
 */
export function acceptsContractAmendment(status: string): boolean {
  return status === 'ACTIVE' || status === 'SUSPENDED' || status === 'EXPIRED'
}
